import React from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Badge } from "@/shared/components/ui/badge";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Route, CheckCircle2, XCircle, ArrowRight, Zap } from "lucide-react";
import ResellerLayout from "@/shared/components/reseller/ResellerLayout";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { getProviderName as getProviderNameUtil } from "@/shared/utils";

export default function ResellerRouting() {
  const resellerName = "Asia Gaming Network";

  // TODO: Implement custom API calls
  const { data: rules = [], isLoading } = useQuery({
    queryKey: ["routing-rules"],
    queryFn: async () => [], // TODO: Implement RoutingRule.list API call
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => [], // TODO: Implement Provider.list API call
  });

  const { language, t } = useLanguage();
  const getProviderName = (code) => {
    const provider = providers.find(p => p.code === code);
    if (!provider) return code;
    return getProviderNameUtil(provider, language);
  };

  return (
    <ResellerLayout currentPage="ResellerRouting" resellerName={resellerName}>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('routingRulesTitle')}</h1>
          <p className="text-slate-500">{t('viewTransactionRoutingConfig')}</p>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-slate-500">{t('loading')}</div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 text-slate-500">{t('noRoutingRules')}</div>
        ) : (
          <div className="space-y-4">
            {rules.map((rule, i) => {
              const providerSequence = rule.provider_sequence ? JSON.parse(rule.provider_sequence) : [];
              return (
                <motion.div
                  key={rule.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  <Card>
                    <CardContent className="p-5">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                            <Route className="h-5 w-5 text-violet-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{rule.name}</h3>
                            <p className="text-sm text-slate-500">{t('priority')}: {rule.priority}</p>
                          </div>
                        </div>
                        <Badge className={rule.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"}>
                          {rule.status === "active" ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                          {rule.status === "active" ? t('active') : t('inactive')}
                        </Badge>
                      </div>

                      <div className="flex flex-wrap gap-2 mb-4">
                        <Badge variant="outline">{rule.transaction_type === "deposit" ? t('deposit') : rule.transaction_type === "withdrawal" ? t('withdrawal') : t('allTypes')}</Badge>
                        <Badge variant="outline">{rule.payment_method?.replace(/_/g, " ") || t('allMethods')}</Badge>
                        <Badge variant="outline">{rule.routing_mode}</Badge>
                        {rule.failover_enabled && (
                          <Badge className="bg-blue-100 text-blue-700">
                            <Zap className="h-3 w-3 mr-1" />
                            {t('failover')}
                          </Badge>
                        )}
                      </div>

                      {providerSequence.length > 0 && (
                        <div>
                          <p className="text-xs text-slate-500 mb-2">{t('providerSequence')}</p>
                          <div className="flex flex-wrap items-center gap-2">
                            {providerSequence.map((code, idx) => (
                              <React.Fragment key={code}>
                                <Badge variant="outline" className="font-medium">
                                  {getProviderName(code)}
                                </Badge>
                                {idx < providerSequence.length - 1 && (
                                  <ArrowRight className="h-4 w-4 text-slate-300" />
                                )}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                      )}

                      {(rule.min_amount > 0 || rule.max_amount < 999999999) && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <p className="text-xs text-slate-500">
                            {t('amountRange')}: RS{rule.min_amount?.toLocaleString()} - RS{rule.max_amount?.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </ResellerLayout>
  );
}